import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function App() {
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('Home');
  const [selectedGender, setSelectedGender] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(true);

  // Cart & Product Detail View States
  const [cart, setCart] = useState([]);
  const [activeProduct, setActiveProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('L');
  const [selectedColor, setSelectedColor] = useState('');
  const [showCartDrawer, setShowCartDrawer] = useState(false);

  // Admin Modal States
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminPass, setAdminPass] = useState('');
  const [newProd, setNewProd] = useState({
    title: '', category: 'Oversized Collection', gender: 'Men',
    price: '', mrp: '', fabric_details: '100% Super Combed Cotton',
    gsm_details: '240 GSM Heavyweight', available_sizes: 'S, M, L, XL, XXL',
    available_colors: 'Black, Washed Gray, Off-White', main_image: '', description: ''
  });

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedGender]);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      let url = `/redyez_api.php?category=${selectedCategory}&gender=${selectedGender}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.status === 'success') {
        setProducts(result.data);
      }
    } catch (err) {
      console.error('Error fetching REDYEZ catalog:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = (product, size, color) => {
    const itemColor = color || (product.available_colors.split(',')[0].trim());
    const itemSize = size || 'L';
    const cartId = `${product.id}-${itemSize}-${itemColor}`;

    setCart((prev) => {
      const exists = prev.find((i) => i.cartId === cartId);
      if (exists) {
        return prev.map((i) => (i.cartId === cartId ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, { ...product, cartId, chosenSize: itemSize, chosenColor: itemColor, qty: 1 }];
    });
    setShowCartDrawer(true);
  };

  const cartTotal = cart.reduce((acc, i) => acc + i.price * i.qty, 0);

  // WhatsApp Immediate Checkout
  const handleWhatsAppOrder = () => {
    let msg = `*NEW ORDER - REDYEZ LUXURY CLOTHING*%0A%0A`;
    cart.forEach((item, idx) => {
      msg += `${idx + 1}. *${item.title}*%0A   • Size: ${item.chosenSize} | Color: ${item.chosenColor} | Qty: ${item.qty}%0A   • Price: ₹${item.price * item.qty}%0A%0A`;
    });
    msg += `*TOTAL AMOUNT: ₹${cartTotal}*%0A%0A_Please confirm availability and dispatch details._`;
    window.open(`https://wa.me/919876543210?text=${msg}`, '_blank');
  };

  // Admin Add Product Handler
  const handleAdminAddProduct = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/redyez_api.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newProd, admin_password: adminPass })
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('Product Added to REDYEZ Catalog!');
        fetchProducts();
        setShowAdminModal(false);
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert('Error adding product');
    }
  };

  return (
    <div className="bg-black text-white min-vh-100 font-sans">
      
      {/* 1. ANNOUNCEMENT BAR */}
      <div className="bg-warning text-dark text-center py-1 font-monospace fw-bold small">
        ⚡ REDYEZ FASHION FESTIVAL: FLAT 40% OFF ON OVERSIZED TEES | USE CODE: REDYEZ10
      </div>

      {/* 2. LUXURY HEADER NAVBAR */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-black sticky-top border-bottom border-dark py-3">
        <div className="container">
          <a className="navbar-brand fw-black fs-3 tracking-widest text-uppercase" href="#home" onClick={() => { setActiveTab('Home'); setSelectedCategory('All'); }}>
            <span className="text-danger fw-extrabold">RED</span>YEZ <span className="fs-6 text-muted font-monospace">LUXURY</span>
          </a>

          {/* Desktop Navigation Links */}
          <div className="d-none d-lg-flex gap-4 fw-semibold text-uppercase fs-7 tracking-wider">
            {['Home', 'Shop', 'Men', 'Women', 'Oversized Collection', 'Polo Collection', 'New Arrivals'].map((nav) => (
              <span
                key={nav}
                className={`cursor-pointer ${activeTab === nav ? 'text-warning border-bottom border-warning pb-1' : 'text-light-50 hover-white'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setActiveTab(nav);
                  if (nav === 'Men' || nav === 'Women') setSelectedGender(nav);
                  else setSelectedGender('All');

                  if (nav.includes('Collection') || nav === 'New Arrivals') setSelectedCategory(nav);
                  else setSelectedCategory('All');
                }}
              >
                {nav}
              </span>
            ))}
          </div>

          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-danger btn-sm" onClick={() => setShowAdminModal(true)}>
              🔒 Admin Panel
            </button>
            <button className="btn btn-warning fw-bold position-relative px-3" onClick={() => setShowCartDrawer(true)}>
              🛍️ BAG ({cart.reduce((a, c) => a + c.qty, 0)})
            </button>
          </div>
        </div>
      </nav>

      {/* 3. HERO BANNER SECTION (VIDEO/IMAGE HYBRID) */}
      {activeTab === 'Home' && (
        <div className="position-relative bg-dark py-5 text-center overflow-hidden border-bottom border-secondary"
             style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(17,24,39,1) 100%), url("https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=1600") center/cover' }}>
          <div className="container py-5">
            <span className="badge bg-danger text-uppercase px-3 py-2 tracking-widest mb-3">Redefining Urban Fashion</span>
            <h1 className="display-2 fw-extrabold text-uppercase tracking-wider text-white mb-3">
              WEAR THE <span className="text-danger">ATTITUDE</span>
            </h1>
            <p className="lead text-light-50 max-w-2xl mx-auto mb-4">
              240 GSM Heavyweight Premium Streetwear • Bio-Washed Anti-Shrink Fabrics • Modern Tailored Cuts
            </p>
            <div className="d-flex justify-content-center gap-3">
              <button className="btn btn-warning btn-lg fw-bold px-4 text-uppercase rounded-0" onClick={() => setActiveTab('Shop')}>
                Explore Catalog
              </button>
              <button className="btn btn-outline-light btn-lg fw-bold px-4 text-uppercase rounded-0" onClick={() => { setActiveTab('Oversized Collection'); setSelectedCategory('Oversized Collection'); }}>
                Oversized Drop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. PRODUCT CATALOG & GRID */}
      <div className="container my-5">
        
        {/* Category Filters Bar */}
        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom border-dark flex-wrap gap-2">
          <h4 className="fw-bold text-uppercase mb-0 tracking-wider">
            {selectedCategory !== 'All' ? selectedCategory : 'Latest Apparel Drop'}
          </h4>
          <div className="d-flex gap-2">
            {['All', 'Oversized Collection', 'Polo Collection', 'New Arrivals'].map((cat) => (
              <button
                key={cat}
                className={`btn btn-sm ${selectedCategory === cat ? 'btn-danger' : 'btn-dark text-light-50'} rounded-0 px-3`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status"></div>
            <p className="mt-2 text-muted font-monospace">LOADING REDYEZ CATALOG...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-5 bg-dark rounded border border-secondary text-muted">
            <h5>No REDYEZ Apparel Found in this Category!</h5>
          </div>
        ) : (
          <div className="row g-4">
            {products.map((p) => (
              <div key={p.id} className="col-6 col-md-4 col-lg-3">
                <div className="card h-100 bg-dark text-white border-secondary rounded-0 overflow-hidden shadow">
                  
                  {/* Badges */}
                  <div className="position-absolute top-0 start-0 m-2 d-flex flex-column gap-1">
                    <span className="badge bg-danger text-uppercase px-2 py-1 font-monospace">{p.gsm_details}</span>
                    {p.mrp > p.price && (
                      <span className="badge bg-warning text-dark font-monospace fw-bold">
                        {Math.round(((p.mrp - p.price) / p.mrp) * 100)}% OFF
                      </span>
                    )}
                  </div>

                  <img
                    src={p.main_image}
                    className="card-img-top rounded-0"
                    alt={p.title}
                    style={{ height: '320px', objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => { setActiveProduct(p); setSelectedSize(p.available_sizes.split(',')[0].trim()); }}
                  />

                  <div className="card-body d-flex flex-column p-3">
                    <small className="text-danger font-monospace text-uppercase fw-bold mb-1">{p.fabric_details}</small>
                    <h6 className="fw-bold mb-1 text-truncate" style={{ cursor: 'pointer' }} onClick={() => setActiveProduct(p)}>
                      {p.title}
                    </h6>

                    <div className="d-flex align-items-center justify-content-between mt-auto pt-2 border-top border-secondary">
                      <div>
                        <span className="fs-5 fw-bold text-white">₹{p.price}</span>
                        {p.mrp > p.price && (
                          <small className="text-decoration-line-through text-muted ms-2">₹{p.mrp}</small>
                        )}
                      </div>
                      <button
                        className="btn btn-warning btn-sm fw-bold rounded-0 px-3"
                        onClick={() => addToCart(p)}
                      >
                        + ADD
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 5. PRODUCT DETAIL MODAL (PDP - ULTRA HD VIEW) */}
      {activeProduct && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content bg-dark text-white border-secondary rounded-0">
              <div className="modal-header border-secondary">
                <span className="badge bg-danger text-uppercase font-monospace">{activeProduct.category}</span>
                <button type="button" className="btn-close btn-close-white" onClick={() => setActiveProduct(null)}></button>
              </div>

              <div className="modal-body p-4">
                <div className="row gy-4">
                  <div className="col-md-6">
                    <img src={activeProduct.main_image} alt={activeProduct.title} className="w-100 rounded border border-secondary" style={{ maxHeight: '500px', objectFit: 'cover' }} />
                  </div>

                  <div className="col-md-6 d-flex flex-column">
                    <h3 className="fw-extrabold text-uppercase">{activeProduct.title}</h3>
                    
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <span className="fs-3 fw-bold text-warning">₹{activeProduct.price}</span>
                      <span className="fs-5 text-decoration-line-through text-muted">₹{activeProduct.mrp}</span>
                      <span className="badge bg-success font-monospace">IN STOCK</span>
                    </div>

                    <div className="p-3 bg-black border border-secondary mb-3">
                      <div className="row text-center font-monospace small">
                        <div className="col-6 border-end border-secondary">
                          <span className="text-muted d-block">FABRIC</span>
                          <strong className="text-white">{activeProduct.fabric_details}</strong>
                        </div>
                        <div className="col-6">
                          <span className="text-muted d-block">WEIGHT / GSM</span>
                          <strong className="text-white">{activeProduct.gsm_details}</strong>
                        </div>
                      </div>
                    </div>

                    <p className="text-light-50 small mb-3">{activeProduct.description}</p>

                    {/* Size Selector */}
                    <div className="mb-4">
                      <label className="fw-bold d-block small mb-2 text-uppercase font-monospace">Select Size:</label>
                      <div className="d-flex gap-2">
                        {activeProduct.available_sizes.split(',').map((sz) => {
                          const sizeClean = sz.trim();
                          return (
                            <button
                              key={sizeClean}
                              className={`btn rounded-0 fw-bold px-3 ${selectedSize === sizeClean ? 'btn-warning' : 'btn-outline-light'}`}
                              onClick={() => setSelectedSize(sizeClean)}
                            >
                              {sizeClean}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-auto d-grid gap-2">
                      <button className="btn btn-warning btn-lg rounded-0 fw-bold text-uppercase" onClick={() => { addToCart(activeProduct, selectedSize); setActiveProduct(null); }}>
                        Add to Bag (₹{activeProduct.price})
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. SHOPPING BAG DRAWER */}
      {showCartDrawer && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
          <div className="modal-dialog modal-md modal-dialog-scrollable ms-auto m-0 h-100">
            <div className="modal-content bg-black text-white border-0 h-100 rounded-0">
              <div className="modal-header border-secondary">
                <h5 className="modal-title fw-bold text-uppercase">YOUR BAG ({cart.length})</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCartDrawer(false)}></button>
              </div>

              <div className="modal-body">
                {cart.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <h5>YOUR BAG IS EMPTY</h5>
                  </div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {cart.map((item) => (
                      <li key={item.cartId} className="list-group-item bg-dark text-white border-secondary d-flex justify-content-between align-items-center my-1 p-3">
                        <div>
                          <h6 className="fw-bold mb-1">{item.title}</h6>
                          <small className="text-muted d-block font-monospace">SIZE: {item.chosenSize} | QTY: {item.qty}</small>
                          <span className="text-warning fw-bold">₹{item.price * item.qty}</span>
                        </div>
                        <button className="btn btn-outline-danger btn-sm rounded-0" onClick={() => setCart(cart.filter(i => i.cartId !== item.cartId))}>×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {cart.length > 0 && (
                <div className="modal-footer border-secondary p-3 bg-dark d-flex flex-column">
                  <div className="d-flex justify-content-between w-100 mb-3 fs-5 fw-bold font-monospace">
                    <span>SUBTOTAL:</span>
                    <span className="text-warning">₹{cartTotal}</span>
                  </div>
                  <button className="btn btn-success btn-lg w-100 rounded-0 fw-bold text-uppercase" onClick={handleWhatsAppOrder}>
                    📲 PROCEED VIA WHATSAPP (₹{cartTotal})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 7. ADMIN MODAL */}
      {showAdminModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content bg-dark text-white border-secondary rounded-0">
              <div className="modal-header border-secondary">
                <h5 className="modal-title fw-bold">🔒 REDYEZ ADMIN PANEL</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAdminModal(false)}></button>
              </div>

              <div className="modal-body p-4">
                <div className="mb-3">
                  <label className="form-label small font-monospace">ADMIN PASSWORD</label>
                  <input type="password" className="form-control bg-black text-white border-secondary rounded-0" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} placeholder="RedyezAdmin@2026" />
                </div>

                <form onSubmit={handleAdminAddProduct}>
                  <h6 className="fw-bold border-bottom border-secondary pb-2 mb-3">ADD NEW PRODUCT TO CATALOG</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small">Product Title *</label>
                      <input type="text" className="form-control bg-black text-white border-secondary rounded-0" required value={newProd.title} onChange={(e) => setNewProd({ ...newProd, title: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Category *</label>
                      <select className="form-select bg-black text-white border-secondary rounded-0" value={newProd.category} onChange={(e) => setNewProd({ ...newProd, category: e.target.value })}>
                        <option value="Oversized Collection">Oversized Collection</option>
                        <option value="Polo Collection">Polo Collection</option>
                        <option value="New Arrivals">New Arrivals</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Price (₹) *</label>
                      <input type="number" className="form-control bg-black text-white border-secondary rounded-0" required value={newProd.price} onChange={(e) => setNewProd({ ...newProd, price: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">MRP / Cut Price (₹)</label>
                      <input type="number" className="form-control bg-black text-white border-secondary rounded-0" value={newProd.mrp} onChange={(e) => setNewProd({ ...newProd, mrp: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Fabric Details</label>
                      <input type="text" className="form-control bg-black text-white border-secondary rounded-0" value={newProd.fabric_details} onChange={(e) => setNewProd({ ...newProd, fabric_details: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">GSM Details</label>
                      <input type="text" className="form-control bg-black text-white border-secondary rounded-0" value={newProd.gsm_details} onChange={(e) => setNewProd({ ...newProd, gsm_details: e.target.value })} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Main Image URL *</label>
                      <input type="text" className="form-control bg-black text-white border-secondary rounded-0" required value={newProd.main_image} onChange={(e) => setNewProd({ ...newProd, main_image: e.target.value })} />
                    </div>
                    <div className="col-12">
                      <button type="submit" className="btn btn-warning w-100 fw-bold rounded-0">
                        ADD PRODUCT TO REDYEZ
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="py-4 bg-black border-top border-dark text-center small text-muted font-monospace">
        <div className="container">
          <p className="mb-1">© 2026 REDYEZ LUXURY APPAREL. ALL RIGHTS RESERVED.</p>
          <p className="mb-0">DESIGNED FOR URBAN STREETWEAR STANDARDS.</p>
        </div>
      </footer>

    </div>
  );
}